/*
 * This component should be used when you need a list of similar components with a "Add more" button.
 *
 * Paarmeters:
 *
 * component - String with component name (component must be declared on globals.js under `window.availableReactComponents`.
 * componentProps - Array of component props to be passed to items in container.
 * messageOnEmpty - Message show when there's no items in the container.
 * defaults - Defaults properties to set on newly created items.
 *
 * This component doesn't handle item deletions, i.e. the `items` state never shrinks. It's up to the container items
 * to handle component removal. This approach was chossen because usually when destroying a item in rails you need to
 * pass the `id` and a flag `_destroy=1`, besides each component can better decide how to render their remove button.
 *
 * The Container always pass the attribute `undercontainer=true` to container items, so they can decide to render or not
 * the delete/destroy button based on this.
 *
 * For an exemple of use, see RedisConfig component.
 */
import React, { Fragment, useState, useCallback } from 'react';
import { Button } from 'semantic-ui-react';
import * as uuid from 'uuid';
import AvailableComponents from './AvailableComponents.jsx';

export default function Container({
  component,
  componentProps = [],
  messageOnEmpty = 'No Items.',
  defaults = {},
}) {
  let ContainerComponent = AvailableComponents[component],
    givenProps = {},
    contents;

  const getComponentProperties = (compProps) => {
    return compProps.map((props) => ({ ...props, key: uuid.v4() }));
  };

  if (AvailableComponents[component]) {
    givenProps.undercontainer = true;
    givenProps.currentCustomer = currentCustomer;
  }

  if (
    !ContainerComponent &&
    React.isValidElement(React.createElement(component))
  ) {
    ContainerComponent = component;
  }

  if (!ContainerComponent) {
    throw new Error(`Unknown component React ${component}.`);
  }

  if (document.head.querySelector('meta[name="csrf-token"]')) {
    givenProps.csrfToken = document.head.querySelector(
      'meta[name="csrf-token"]',
    ).content;
  }

  const [items, setItems] = useState(() =>
    getComponentProperties(componentProps),
  );

  const onAddItem = useCallback(
    () => setItems([...items, { ...defaults, ...givenProps, key: uuid.v4() }]),
    [items],
  );

  if (items.length == 0) {
    contents = <p>{messageOnEmpty}</p>;
  } else {
    contents = items.map((propValues) => {
      return (
        <ContainerComponent
          key={propValues.key}
          undercontainer={true}
          {...defaults}
          {...propValues}
          {...givenProps}
        />
      );
    });
  }

  return (
    <Fragment>
      {contents}
      <Button
        primary
        as='a'
        className='multiple-value-button'
        content='Add more'
        icon='plus circle'
        onClick={onAddItem}
      />
    </Fragment>
  );
}
